import * as types from '../constants/cidraActions';
import {
  doLogout,
  doStartIgnoreTimeout,
  doEndIgnoreTimeout,
  doSetInterplexAddress,
  doEndDeviceManagementSession,
  doStartDeviceManagementSession,
  doReset
} from './shared';
import {
  doStartSession, doStartAuthSession, doStartAuthSessionOnly
} from './startSession';
import {
  doSkipEnrollment, doEnrollSms, doEnrollImprivataId, doEnrollVerifySms, doSmsEnrolled, doImprivataIdEnrolled, doSubmitAfterSSDM, doUpdateDeviceList
} from './enrollment';
import {
  doChangeEnrollModality, doChangeModality, doInputIIDEnroll, setChooseModality
} from './chooseModality';
import {
  doVerifyPassword, doVerifyPin, doVerifyTempCode, doVerifySms, doVerifyImprivataIdManual,
  doVerifyChangePassword, doInitiateImprivataIdPush, doResendSms, doVerifyOtp, doVerifyEmail,
  doResendEmail, doVerifyIcpFace, doInitiateIdv
} from './authentication';
import { doToggleSkipSecondFactor } from './skipSecondFactor';
import {
  doGetResumeAuthFlowToken,
  doStartResumeAuthFlow
} from './resumeAuthFlow';
import { doStartAuthenticationSession, doStartResumeAuthSession } from './resumeAuthSession';

/**
 * Starts session with applex.
 *
 * @export
 * @param {string} accountToken Token assigned via provisioning.
 * @param {string} cloudauthEnvironment Currently "dev" or "integration".
 * @returns
 */
export function startSession(accountToken, cloudauthEnvironment, selfServiceEnvironment, username, workflow, cidMobileServiceInfo) {
  return function(dispatch) {
    return dispatch(doStartSession(accountToken, cloudauthEnvironment, selfServiceEnvironment, username, workflow, cidMobileServiceInfo) );
  };
}

/**
 * resume session with applex.
 *
 * @export
 * @param {string} jti account token key.
 * @returns
 */
export function resumeSession(jti) {
  return function(dispatch) {
    return dispatch(doStartAuthenticationSession(jti) );
  };
}

/**
 * Starts the device management workflow.
 *
 * @export
 * @returns
 */
export function startDeviceManagementSession() {
  return { type: types.START_DEVICE_MANAGEMENT_WORKFLOW, };
}

export function actuallyStartDeviceManagementSession() {
  return function(dispatch){
    dispatch(doStartDeviceManagementSession() );
  };
}

export function actuallyEndDeviceManagementSession() {
  return function(dispatch){
    dispatch(doEndDeviceManagementSession() );
  };
}

/**
 * Ends the device management workflow.
 *
 * @export
 * @returns
 */
export function endDeviceManagementSession() {
  return { type: types.END_DEVICE_MANAGEMENT_WORKFLOW, };
}

/**
 * Sets account token that ties application session to a onesign appliance.
 *
 * @export
 * @param {string} accountToken Token assigned via provisioning.
 * @param {string} cloudauthEnvironment Currently "dev" or "integration".
 * @returns
 */
export function setAccountToken(accountToken, cloudauthEnvironment, selfServiceEnvironment) {
  return {
    type: types.SET_ACCOUNT_TOKEN,
    accountToken,
    cloudauthEnvironment,
    selfServiceEnvironment,
  };
}

/**
 * Sets the optionalWorkflowName and requestContext
 *
 * @export
 * @param {string} optionalWorkflowName the initial optionalWorkflowName
 * @param {string} requestContext the request context to pass additional parameters to the EAM server
 */
export function setOptionalWorkflowNameAndRequestContext(optionalWorkflowName, requestContext) {
  return {
    type: types.SET_OPTIONAL_WORKFLOW_NAME_AND_REQUEST_CONTEXT,
    optionalWorkflowName,
    requestContext,
  };
}

/**
 * Sets the resumeAuthFlowTokenUrl
 *
 * @export
 * @param {string} resumeAuthFlowTokenUrl the initial resumeAuthFlowTokenUrl
 */
export function setResumeAuthFlowTokenUrl(resumeAuthFlowTokenUrl) {
  return {
    type: types.SET_RESUME_AUTH_FLOW_TOKEN_URL,
    resumeAuthFlowTokenUrl,
  };
}

/**
 * Sets the forceMfa
 *
 * @export
 * @param {boolean} forceMfa the initial forceMfa
 */
export function setForceMfa(forceMfa) {
  return {
    type: types.SET_FORCE_MFA,
    forceMfa,
  };
}

/**
 * Sets the username which will be prepopulated on the first screen
 *
 * @export
 * @param {string} username the initial username
 */
export function setInitialUsername(username) {
  return {
    type: types.SET_INITIAL_USERNAME,
    username,
  };
}

/**
 * Sets the password which will be prepopulated on the first screen
 *
 * @export
 * @param {string} password the initial password
 */
export function setInitialPassword(password) {
  return {
    type: types.SET_INITIAL_PASSWORD,
    password,
  };
}

/**
 * Sets the errorHandlerUrl
 *
 * @export
 * @param {string} errorHandlerUrl the errorHandlerUrl
 */
export function setErrorHandlerUrl(errorHandlerUrl) {
  return {
    type: types.SET_ERROR_HANDLER_URL,
    errorHandlerUrl,
  };
}

/**
 * Starts an individual user session to begin authentication.
 *
 * @export
 * @param {string} inputUsername Username of user
 * @param {string} credential Password
 * @returns
 */
export function startAuthSession(inputUsername, credential) {
  return function(dispatch) {
    return dispatch(doStartAuthSession(inputUsername, credential) );
  };
}

/**
 * Starts an individual user session to begin authentication.
 *
 * @export
 * @param {string} inputUsername Username of user
 * @returns
 */
export function startAuthSessionOnly(inputUsername) {
  return function(dispatch) {
    return dispatch(doStartAuthSessionOnly(inputUsername) );
  };
}

/**
 * Log user out of application.
 *
 * @export
 * @returns
 */
export function logout() {
  return function(dispatch) {
    dispatch(doLogout() );
    return Promise.resolve();
  };
}

/**
 * Reset to initial state with username.
 *
 * @export
 * @returns
 */
export function reset() {
  return function(dispatch) {
    dispatch(doReset() );
    return Promise.resolve();
  };
}

/**
 * Changes currently logged in user's password.
 *
 * @export
 * @param {string} newPassword New password to change to.
 * @returns
 */
export function changePassword(newPassword) {
  return function(dispatch) {
    return dispatch(doVerifyChangePassword(newPassword) );
  };
}

/**
 * Verify password matches value in onesign for the user.
 *
 * @export
 * @param {string} password Password to check
 * @returns
 */
export function verifyPassword(password) {
  return function(dispatch) {
    return dispatch(doVerifyPassword(password) );
  };
}

/**
 * Verify pin matches value in onesign.
 *
 * @export
 * @param {string} pin Pin to check
 * @returns
 */
export function verifyPin(pin) {
  return function(dispatch) {
    return dispatch(doVerifyPin(pin) );
  };
}

/**
 * Verify the temporary code matches the value from onesign admin console.
 *
 * @export
 * @param {string} tempcode Temorary code generated in onesign admin console
 * @returns
 */
export function verifyTempCode(tempcode) {
  return function(dispatch) {
    return dispatch(doVerifyTempCode(tempcode) );
  };
}

/**
 * Verify the temporary code matches the value from email.
 *
 * @export
 * @param {string} tempcode Temorary code input from email
 * @returns
 */
export function verifyEmail(tempcode) {
  return function(dispatch) {
    return dispatch(doVerifyEmail(tempcode) );
  };
}

/**
 * Verify the IcpFace verification code.
 *
 * @export
 * @param {string} icpVerificationCode code input from Icp
 * @returns
 */
export function verifyIcpFace(icpVerificationCode) {
  return function(dispatch) {
    return dispatch(doVerifyIcpFace(icpVerificationCode) );
  };
}

/**
 * Verify a one-time code received from an SMS message.
 *
 * @export
 * @param {number} tokenCode One-time code in the SMS
 * @returns
 */
export function verifySms(tokenCode) {
  return function(dispatch) {
    return dispatch(doVerifySms(tokenCode) );
  };
}

/**
 * Verify one-time pin value matches value in onesign.
 *
 * @export
 * @param {string} tokenCode Pin value to confirm
 * @returns
 */
export function verifyOtp(tokenCode) {
  return function(dispatch) {
    return dispatch(doVerifyOtp(tokenCode) );
  };
}

/**
 * Verify a manually entered one-time code from the IID app
 * after the push from IID has timed out.
 *
 * @export
 * @param {number} input One-time code from Imprivata ID app
 * @returns
 */
export function verifyImprivataIdManual(input) {
  return function(dispatch) {
    return dispatch(doVerifyImprivataIdManual(input) );
  };
}

/**
 * Changes user from one kind of login factor to another.
 *
 * @export
 * @param {Modality} fromModality
 * @param {Modality} toModality
 * @returns
 */
export function changeModality(fromModality, toModality) {
  return function(dispatch) {
    return dispatch(doChangeModality(fromModality, toModality) );
  };
}
/**
 * @typedef {('otp'|'iid'|'pin'|'sms'|'password'|'tempcode')} Modality Current modalities
 */

/**
 * Resends the one-time code for SMS authentication.
 *
 * @export
 * @returns
 */
export function resendSms() {
  return function(dispatch) {
    return dispatch(doResendSms() );
  };
}

/**
 * Resends the one-time code for email authentication.
 *
 * @export
 * @returns
 */
export function resendEmail() {
  return function(dispatch) {
    return dispatch(doResendEmail() );
  };
}


/**
 * Send an IID push notification to the current user. Currently
 * only used when the user clicks retry on the IID push page. With all
 * other places the push happens automatically.
 *
 * @export
 * @returns
 */
export function initiateImprivataIdPush() {
  return function(dispatch) {
    return dispatch(doInitiateImprivataIdPush() );
  };
}

/**
 * Initiate Idv procedure.
 *
 * @export
 * @returns
 */
export function initiateIdv() {
  return function(dispatch) {
    return dispatch(doInitiateIdv() );
  };
}

/**
 * Navigate to and Start IID enrollment.
 *
 * @export
 * @returns
 */
export function initiateInputEnrollImprivataId(canSkipEnrollment, canChangeEnrollmentModality) {
  return function(dispatch) {
    return dispatch(doInputIIDEnroll(canSkipEnrollment, canChangeEnrollmentModality) );
  };
}

/**
 * Start IID enrollment.
 *
 * @export
 * @returns
 */
export function inputEnrollImprivataId() {
  return { type: types.START_INPUT_ENROLL_IMPRIVATA_ID, };
}

/**
 * Move enrollment flow back to the introduction view.
 *
 * @export
 * @returns
 */
export function backToIntroduction() {
  return { type: types.BACK_TO_INTRODUCTION, };
}

/**
 * Skip enrollment for the user and just authenticates.
 *
 * @export
 * @returns
 */
export function skipEnrollment() {
  return function(dispatch) {
    return dispatch(doSkipEnrollment() );
  };
}

/**
 * Enroll an Imprivata ID application for a user.
 *
 * @export
 * @param {string} serialNumber The serial number for the particular IID app.
 * Typically starts with the letters, "IMPR".
 * @param {number} tokenCode The one-time code generated in the IID app.
 * @returns
 */
export function enrollImprivataId(serialNumber, tokenCode) {
  return function(dispatch) {
    return dispatch(doEnrollImprivataId(serialNumber, tokenCode) );
  };
}

/**
 * Called when Imprivata ID application has completed
 * enrollment and this application is ready to move on
 * to the next step.
 *
 * @export
 * @returns
 */
export function imprivataIdEnrolled() {
  return function(dispatch) {
    return dispatch(doImprivataIdEnrolled() );
  };
}

/**
 * Start SMS enrollment.
 *
 * @export
 * @returns
 */
export function inputEnrollSms() {
  return { type: types.START_INPUT_ENROLL_SMS, };
}

/**
 * Enrolls a phone number in sms.
 *
 * @export
 * @param {string} input The phone number to enroll
 * @returns
 */
export function enrollSms(input) {
  return function(dispatch) {
    return dispatch(doEnrollSms(input) );
  };
}

/**
 * Verify that the one-time code sent to the phone matches
 * matches the code on the server to verify the phone number
 * entered can receive a text message.
 *
 * @export
 * @param {number} input The one-time pin sent to the phone to verify the
 * previous phone number was entered correctly.
 * @returns
 */
export function enrollVerifySms(input) {
  return function(dispatch) {
    return dispatch(doEnrollVerifySms(input) );
  };
}

/**
 * Move enrollment flow back to the add phone number view.
 *
 * @export
 * @returns
 */
export function editPhoneNumber() {
  return { type: types.EDIT_PHONE_NUMBER, };
}

/**
 * Called when a phone number has completed SMS
 * enrollment and this application is ready to move on
 * to the next step.
 *
 * @export
 * @returns
 */
export function smsEnrolled() {
  return function(dispatch) {
    return dispatch(doSmsEnrolled() );
  };
}

export function submitAfterSSDM() {
  return function(dispatch) {
    return dispatch(doSubmitAfterSSDM() );
  };
}

export function updateDeviceList(deviceList) {
  return function(dispatch) {
    return dispatch(doUpdateDeviceList(deviceList) );
  };
}

/**
 * Switches from one type of enrollment to another.
 *
 * @export
 * @param {EnrollModality} modality
 * @returns
 */
export function changeEnrollModality(modality) {
  return function(dispatch) {
    return dispatch(doChangeEnrollModality(modality) );
  };
}
/**
 * @typedef {('iid'|'sms')} EnrollModality Current enrollment modalities
 */

/**
  * @export
  * @param {*} toggleState whether to enable or disable Skip Second Factor for the user
  * @returns
  */
export function toggleSkipSecondFactor(toggleState) {
  return function(dispatch) {
    return dispatch(doToggleSkipSecondFactor(toggleState) );
  };
}

/**
  * Starts ignoring timeout events for things like IID push timeout
  */
export function startIgnoreTimeout() {
  return function(dispatch) {
    return dispatch(doStartIgnoreTimeout() );
  };
}

/**
  * Stop ignoring timeout events for things like IID push timeout
  */
export function endIgnoreTimeout() {
  return function(dispatch) {
    return dispatch(doEndIgnoreTimeout() );
  };
}

/**
  * Set the interplex address for the current self-service session
  */
export function setInterplexAddress(interplexAddress) {
  return function(dispatch) {
    return dispatch(doSetInterplexAddress(interplexAddress) );
  };
}

/**
 * Sets whether the chooseModality screen is coming from the more options link.
 * @param moreOptionsChooseModality
 * @returns
 */

export function moreOptionsChooseModality(moreOptionsChooseModality) {
  return function(dispatch) {
    return dispatch(setChooseModality(moreOptionsChooseModality) );
  };
}

export function getResumeAuthFlowToken() {
  return function(dispatch) {
    return dispatch(doGetResumeAuthFlowToken() );
  };
}

export function startResumeAuthFlow() {
  return function(dispatch) {
    return dispatch(doStartResumeAuthFlow() );
  };
}

export function startResumeAuthSession() {
  return function(dispatch) {
    return dispatch(doStartResumeAuthSession() );
  };
}
